/**
 * mapDeliverySLA — pure mapping layer.
 *
 * Converts the raw checkDeliveryDetails (v2) response into the `SlaViewModel`
 * consumed by the presentation layer. This module is intentionally free of any
 * React / redux dependency so it can be unit-tested in isolation and reused by
 * both PDP and Cart.
 */
import moment from 'moment';
import {
  DELIVERY_METHOD,
  DeliveryMethod,
  PRICE_TYPE,
  PriceType,
  SLA_BADGE_LABEL,
  SLA_CONTEXT,
  SlaContext,
  hasBadgePriceType,
  normalizePriceType
} from './constants';
import {
  PriceTypeTags,
  RawCheckDeliveryDetailsV2,
  RawPriceData,
  RawSlaOption,
  SlaCodeLabels,
  SlaOption,
  SlaPriceView,
  SlaViewModel
} from './types';

/**
 * scopedView ships serviceability under either `serviceability` or the
 * misspelled `servicability`; a missing flag means "unknown", treated as
 * serviceable so options never disappear on a partial payload.
 */
const readServiceable = (
  entry?: { serviceability?: boolean; servicability?: boolean } | null
): boolean => entry?.serviceability ?? entry?.servicability ?? true;

/** True when a raw scopedView option can actually be delivered. */
export const isSlaOptionServiceable = (option?: RawSlaOption | null): boolean =>
  readServiceable(option?.serviceabilityDetails) &&
  readServiceable(option?.serviceabilityDetails?.productDetails?.[0]);

/**
 * True when at least one raw option is serviceable. PDP/EDD use this to decide
 * whether the multiseller SLA cards are worth showing at all — with nothing
 * deliverable there is no seller choice to make, so the callers fall back to the
 * legacy EDD copy.
 */
export const hasServiceableSlaOption = (options?: RawSlaOption[] | null): boolean =>
  Array.isArray(options) && options.some(isSlaOptionServiceable);

const normalizeDeliveryMethod = (method?: string): DeliveryMethod => {
  const upper = (method || '').toUpperCase();
  if (upper === DELIVERY_METHOD.RUSH) return DELIVERY_METHOD.RUSH;
  if (upper === DELIVERY_METHOD.PRIORITY) return DELIVERY_METHOD.PRIORITY;
  return DELIVERY_METHOD.STANDARD;
};

const toNumber = (value?: string | number): number => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Prefer the display-formatted value, fall back to the full formatted value. */
const formatPrice = (data?: RawPriceData): string =>
  data?.displayformattedValue || data?.formattedValue || '';

const buildPriceView = (option: RawSlaOption): SlaPriceView => {
  const priceData = option?.price?.priceData;
  const wasData = option?.price?.wasPriceData;
  const price = formatPrice(priceData);
  const mrp = formatPrice(wasData);
  return {
    price,
    discount: priceData?.discountPercent || '',
    // Hide MRP when there is effectively no markdown.
    mrp: mrp && mrp !== price ? mrp : ''
  };
};

/** Day-bucket SLA codes ("2DD", "3DD"…) already read as days, so name the day. */
const isMultiDaySla = (code: string): boolean => /^\d+DD$/.test(code);

/**
 * Express copy straight off the flag config (`multiSellerSlaConfig.slaTimeMessage`),
 * same source and rules as desktop's `formatSlaCode`:
 *   "4HR"   -> "4 hours"
 *   "2DD"   -> "2 Days, Fri"      (day buckets append the EDD weekday)
 *   "90MIN" -> "90 minutes, Mon"  (with `withWeekday` — priority names the day)
 * Empty when the code is missing from the config, so the caller falls back to
 * the plain EDD date.
 */
const formatSlaCode = (
  deliverySLA?: string,
  labels?: SlaCodeLabels,
  eddUpper?: string,
  withWeekday = false
): string => {
  const code = String(deliverySLA || '');
  const label = code && labels?.[code];
  if (!label) return '';
  if (!isMultiDaySla(code) && !withWeekday) return label;

  const edd = eddUpper ? moment(eddUpper) : null;
  if (!edd || !edd.isValid()) return label;
  return `${label}, ${edd.format('ddd')}`;
};

/** ISO date -> "9 Aug", the same plain EDD format the desktop card uses. */
const formatEddDate = (eddUpper: string): string => {
  const edd = eddUpper ? moment(eddUpper) : null;
  return edd && edd.isValid() ? edd.format('D MMM') : '';
};

/**
 * "Delivery in 2 Days, Fri" for PRIORITY / "Delivery in 4 hours" for RUSH, from
 * the configured SLA copy; "Delivery by 9 Aug" for standard — matching the
 * desktop card. Express rows fall back to the date when the flag config has no
 * entry for their SLA code.
 */
const buildTimeline = (
  deliveryMethod: DeliveryMethod,
  eddUpper: string,
  deliverySLA: string,
  slaCodeLabels?: SlaCodeLabels
): { timelineLabel: string; timelineValue: string } => {
  const isPriority = deliveryMethod === DELIVERY_METHOD.PRIORITY;
  const isExpress = isPriority || deliveryMethod === DELIVERY_METHOD.RUSH;
  const slaText = isExpress ? formatSlaCode(deliverySLA, slaCodeLabels, eddUpper, isPriority) : '';

  if (slaText) {
    return { timelineLabel: 'Delivery in', timelineValue: slaText };
  }
  return { timelineLabel: 'Delivery by', timelineValue: formatEddDate(eddUpper) };
};

/**
 * "Order by 09:00PM" / "Order by 5 Aug, 09:00PM". Empty when no cutoff.
 *
 * A cutoff only ships with the express methods, and it is what makes their
 * promise conditional, so it is rendered whenever the payload carries one —
 * same as desktop.
 */
const buildOrderByText = (cutOffTime?: string): string => {
  if (!cutOffTime) return '';
  const cutoff = moment(cutOffTime);
  if (!cutoff.isValid()) return '';
  if (cutoff.isSame(moment(), 'day')) {
    return `Order by ${cutoff.format('hh:mmA')}`;
  }
  return `Order by ${cutoff.format('D MMM, hh:mmA')}`;
};

const resolveBadgeLabel = (
  priceType: PriceType,
  priceTypeTags?: PriceTypeTags,
  option?: RawSlaOption
): string => {
  // Per-option copy wins where the payload carries it (the B2C seller list, one
  // card per seller). An explicit empty string is a deliberate "no badge here",
  // so the key's presence is what counts, not its truthiness.
  if (option && typeof option.tagLabel === 'string') return option.tagLabel;
  // Only BEST_PRICE / BEST_SPEED carry a badge. Every other row — the seller
  // list is mostly rows with no priceType at all — goes without, rather than
  // taking the "BEST PRICE" default `normalizePriceType` lands them on.
  if (option && !hasBadgePriceType(option.priceType)) return '';
  // Otherwise the badge is purely priceType-driven: config-driven copy wins,
  // with the design-system defaults as fallback.
  if (priceType === PRICE_TYPE.FASTEST) {
    return priceTypeTags?.fastest || SLA_BADGE_LABEL.FASTEST;
  }
  return priceTypeTags?.best || SLA_BADGE_LABEL.BEST;
};

const mapOption = (
  option: RawSlaOption,
  index: number,
  context: SlaContext,
  priceTypeTags?: PriceTypeTags,
  slaCodeLabels?: SlaCodeLabels
): SlaOption => {
  const priceType = normalizePriceType(option?.priceType);
  const detail = option?.serviceabilityDetails?.productDetails?.[0] || {};
  const deliveryMethod = normalizeDeliveryMethod(detail.deliveryMethod);
  const eddUpper = detail.eddUpper || '';
  const isSelected = Boolean(option?.isSelected);
  const serviceable = readServiceable(option?.serviceabilityDetails) && readServiceable(detail);

  const { timelineLabel, timelineValue } = buildTimeline(
    deliveryMethod,
    eddUpper,
    detail.deliverySLA || '',
    slaCodeLabels
  );

  return {
    /**
     * Card identity, used for selection and as the React key.
     *
     * Always carries the position, because nothing else on the option is
     * reliably unique: B2C lists every seller for the size and they routinely
     * share a `priceGroupId`, with nearly all of them normalising to the same
     * `priceType`. Selection is by id, so a click on a card whose id already
     * matched the selected one was read as "same card" and dropped — nothing
     * committed, and the add-to-bag call kept the default seller. Keying off the
     * seller instead is not enough either: `sellerId` is sometimes sent only on
     * the option's product detail, and then it is blank here.
     */
    id: `${option?.priceGroupId || option?.priceType || 'sla'}-${index}`,
    priceType,
    badgeLabel: resolveBadgeLabel(priceType, priceTypeTags, option),
    badgeType: option?.badgeType || priceType,
    sellerName: option?.sellerName || detail.sellerName || '',
    // Some responses carry the seller only on the product detail.
    sellerId: option?.sellerId || detail.sellerId || '',
    // Sent as-is on add-to-bag / scoped calls — `priceType` is normalised.
    rawPriceType: option?.priceType || '',
    isSelected,
    serviceable,
    reasonForNotServiceability: detail.reasonForNotServiceability || '',
    codEligible: Boolean(detail.codEligible ?? option?.serviceabilityDetails?.codEligible),
    deliveryMethod,
    timelineLabel,
    timelineValue,
    orderByText: buildOrderByText(detail.cutOffTime),
    deliveryFee: toNumber(detail.deliveryFee),
    price: buildPriceView(option),
    stockLevelStatus: option?.stock?.stockLevelStatus || '',
    raw: option
  };
};

/**
 * Extract the raw option array for the selected SKU from a full v2 response.
 * `sku` is an array of single-key objects, so we take the first SKU entry
 * (a PDP selection resolves to exactly one SKU) unless a `skuId` is given.
 */
export const extractSkuOptions = (
  response?: RawCheckDeliveryDetailsV2 | null,
  skuId?: string
): RawSlaOption[] => {
  const skuList = response?.sku;
  if (!Array.isArray(skuList) || skuList.length === 0) return [];
  if (skuId) {
    const match = skuList.find(entry => entry && entry[skuId]);
    if (match) return match[skuId] || [];
  }
  const first = skuList[0] || {};
  const firstKey = Object.keys(first)[0];
  return firstKey ? first[firstKey] || [] : [];
};

/**
 * Map a raw option array into the presentation view model.
 * Serviceable options are surfaced first (in API order); non-serviceable ones
 * are kept but flagged so the UI can render them in a disabled state.
 */
export const mapSlaOptions = (
  slaOptions?: RawSlaOption[] | null,
  context: SlaContext = SLA_CONTEXT.PDP,
  priceTypeTags?: PriceTypeTags,
  slaCodeLabels?: SlaCodeLabels
): SlaViewModel => {
  const rawOptions = Array.isArray(slaOptions) ? slaOptions : [];
  const options = rawOptions.map((option, index) =>
    mapOption(option, index, context, priceTypeTags, slaCodeLabels)
  );

  const serviceableOptions = options.filter(option => option.serviceable);
  // Sole serviceable card is always the selected option.
  if (serviceableOptions.length === 1) {
    const onlyId = serviceableOptions[0].id;
    options.forEach(option => {
      option.isSelected = option.serviceable && option.id === onlyId;
    });
  }
  const pinCode =
    rawOptions
      .map(option => option?.serviceabilityDetails?.pinCode)
      .find(Boolean) || '';

  return {
    isMultiSla: serviceableOptions.length > 1,
    options,
    pinCode,
    serviceable: serviceableOptions.length > 0,
    reasonForNotServiceability:
      serviceableOptions.length === 0
        ? options[0]?.reasonForNotServiceability || ''
        : ''
  };
};

/** Convenience: full v2 response -> view model. */
export const mapDeliverySLA = (
  response?: RawCheckDeliveryDetailsV2 | null,
  context: SlaContext = SLA_CONTEXT.PDP,
  skuId?: string,
  priceTypeTags?: PriceTypeTags,
  slaCodeLabels?: SlaCodeLabels
): SlaViewModel =>
  mapSlaOptions(extractSkuOptions(response, skuId), context, priceTypeTags, slaCodeLabels);
